// Toolbar above the row-details panel: cell-name search input + hideNulls/group/density toggles.

import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { EyeOff, LayoutGrid, Rows } from 'lucide-react';

export type RowDetailsToolbarState = {
  readonly cellSearch: string;
  readonly hideNulls: boolean;
  readonly nullCellCount: number;
  readonly density: 'compact' | 'normal';
  readonly groupByCategory: boolean;
};

type RowDetailsToolbarProps = {
  readonly state: RowDetailsToolbarState;
  readonly onCellSearchChange: (value: string) => void;
  readonly onToggleHideNulls: () => void;
  readonly onToggleDensity: () => void;
  readonly onToggleGroupByCategory: () => void;
};

export function RowDetailsToolbar({
  state,
  onCellSearchChange,
  onToggleHideNulls,
  onToggleDensity,
  onToggleGroupByCategory,
}: RowDetailsToolbarProps) {
  return (
    <div
      className="flex-shrink-0 flex items-center gap-2 px-2 py-1.5 border-b border-border bg-muted/30 select-none"
      data-testid="row-details-toolbar"
    >
      <Input
        type="search"
        value={state.cellSearch}
        onChange={(e) => onCellSearchChange(e.target.value)}
        placeholder="Search cells…"
        className="h-7 text-xs flex-1 min-w-0"
        data-testid="row-details-cell-search"
      />
      <Button
        type="button"
        variant={state.hideNulls ? 'default' : 'outline'}
        size="sm"
        className="h-7 px-2 text-xs"
        onClick={onToggleHideNulls}
        data-testid="row-details-toggle-hide-nulls"
        aria-pressed={state.hideNulls}
      >
        <EyeOff className="h-3.5 w-3.5 mr-1" />
        Hide nulls ({state.nullCellCount})
      </Button>
      <Button
        type="button"
        variant={state.groupByCategory ? 'default' : 'outline'}
        size="sm"
        className="h-7 px-2 text-xs"
        onClick={onToggleGroupByCategory}
        data-testid="row-details-toggle-group"
        aria-pressed={state.groupByCategory}
      >
        <LayoutGrid className="h-3.5 w-3.5 mr-1" />
        Group
      </Button>
      <Button
        type="button"
        variant={state.density === 'compact' ? 'default' : 'outline'}
        size="sm"
        className="h-7 px-2 text-xs"
        onClick={onToggleDensity}
        data-testid="row-details-toggle-density"
        aria-pressed={state.density === 'compact'}
      >
        <Rows className="h-3.5 w-3.5 mr-1" />
        Density
      </Button>
    </div>
  );
}
